// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet } from 'react-native';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { HolePreviewMedia } from '@/features/tour-preview/HolePreviewMedia';
import { useCourseFlyover } from '@/features/tour-preview/useCourseFlyover';
import { useTourPreview, type PreviewRound } from '@/features/tour-preview/useTourPreview';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';

const formatDate = (isoDate: string | null): string | null => {
  if (!isoDate) return null;
  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
};

/**
 * The tour of the tour: every round the tour will play, its date and its course, and a
 * flyover preview of each course — the look at what is coming that the market research
 * found nowhere else in one place.
 */
export default function TourPreviewScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { tourId: tourParam } = useLocalSearchParams<{ tourId?: string }>();
  const tourId = tourParam ? String(tourParam) : null;

  const preview = useTourPreview(apiBaseUrl, user?.token, tourId);
  const [flyoverCourseId, setFlyoverCourseId] = useState<string | null>(null);
  const flyoverCourse = flyoverCourseId ? (preview.coursesById[flyoverCourseId] ?? null) : null;
  const flyover = useCourseFlyover(
    apiBaseUrl,
    user?.token,
    flyoverCourseId,
    flyoverCourse,
    flyoverCourseId !== null
  );

  if (!user) {
    return <Redirect href="/" />;
  }

  const first = formatDate(preview.firstPlayDate);
  const last = formatDate(preview.lastPlayDate);
  const dateRange = first && last ? (first === last ? first : `${first} – ${last}`) : null;

  const renderRound = (round: PreviewRound) => {
    const course = round.courseId ? (preview.coursesById[round.courseId] ?? null) : null;
    const courseLine = course
      ? [course.clubName, course.city].filter(Boolean).join(', ')
      : null;
    return (
      <View key={round.id} style={styles.roundCard} testID={`preview-round-${round.id}`}>
        <View style={styles.roundHeader}>
          <Text style={styles.roundName}>
            {round.name ?? round.courseName ?? t('tourPreview.title')}
          </Text>
          <Text style={styles.roundDate}>
            {formatDate(round.playDate) ?? t('tourPreview.dateNotSet')}
          </Text>
        </View>
        {round.courseId ? (
          <>
            <Text style={styles.courseName}>{course?.name ?? round.courseName}</Text>
            {courseLine ? <Text style={styles.meta}>{courseLine}</Text> : null}
            {round.holesCount ? (
              <Text style={styles.meta}>
                {t('tourPreview.holesCount', { count: round.holesCount })}
              </Text>
            ) : null}
            <Pressable
              accessibilityRole="button"
              testID={`preview-flyover-${round.id}`}
              style={styles.previewButton}
              onPress={() => setFlyoverCourseId(round.courseId)}
            >
              <Text style={styles.previewButtonText}>{t('tourPreview.playPreview')}</Text>
            </Pressable>
          </>
        ) : (
          <Text style={styles.meta}>{t('tourPreview.courseNotChosen')}</Text>
        )}
      </View>
    );
  };

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
      <View style={styles.headerRow}>
        <Pressable style={styles.backButton} onPress={() => router.back()}>
          <Text style={styles.backText}>←</Text>
        </Pressable>
        <Text style={styles.title}>{t('tourPreview.title')}</Text>
      </View>

      {preview.tourName ? <Text style={styles.tourName}>{preview.tourName}</Text> : null}
      {dateRange ? <Text style={styles.dateRange}>{dateRange}</Text> : null}

      {preview.loading ? <Text style={styles.meta}>{t('tourPreview.loading')}</Text> : null}
      {preview.error ? (
        <View style={styles.errorBox}>
          <Text style={styles.error}>{t('tourPreview.loadFailed')}</Text>
          <Pressable accessibilityRole="button" style={styles.previewButton} onPress={preview.retry}>
            <Text style={styles.previewButtonText}>{t('tourPreview.retry')}</Text>
          </Pressable>
        </View>
      ) : null}

      {preview.rounds.map(renderRound)}

      <Modal
        visible={flyoverCourseId !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setFlyoverCourseId(null)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalBody}>
            <HolePreviewMedia
              apiBaseUrl={apiBaseUrl}
              token={user.token}
              courseId={flyoverCourseId}
              courseName={flyoverCourse?.name ?? ''}
              holes={flyover.holes}
              loading={flyover.loading}
              unavailable={flyover.unavailable}
              onClose={() => setFlyoverCourseId(null)}
            />
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1, backgroundColor: '#01260B' },
  container: { padding: 16, gap: 12, paddingBottom: 48 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: 'transparent' },
  backButton: { padding: 6 },
  backText: { color: '#FFFFFF', fontSize: 20 },
  title: { color: '#FFFFFF', fontSize: 20, fontWeight: '700' },
  tourName: { color: '#FFFFFF', fontSize: 17, fontWeight: '700' },
  dateRange: { color: appColors.text.primary, fontSize: 14 },
  roundCard: {
    backgroundColor: '#07200D',
    borderRadius: 12,
    padding: 14,
    gap: 4,
  },
  roundHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  roundName: { color: '#FFFFFF', fontSize: 15, fontWeight: '700', flexShrink: 1 },
  roundDate: { color: appColors.text.primary, fontSize: 13 },
  courseName: { color: '#FFFFFF', fontSize: 14 },
  meta: { color: appColors.text.primary, fontSize: 13 },
  previewButton: {
    alignSelf: 'flex-start',
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: appColors.accent.primary,
  },
  previewButtonText: { color: '#01260B', fontSize: 13, fontWeight: '700' },
  errorBox: { gap: 8, backgroundColor: 'transparent' },
  error: { color: '#FF8A80', fontSize: 14 },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    padding: 16,
  },
  modalBody: { width: '100%', maxWidth: 520, alignSelf: 'center', backgroundColor: 'transparent' },
});
